/**
 * Timelines: reading a list of dated events and periods, and placing them along a
 * horizontal or vertical axis.
 *
 * Each line is `date | label | description`. A single date is an event — a dot on the
 * axis with a card of text. A range (`1914 – 1918`) is a period — a band beside the
 * axis. Events can be spaced in proportion to time, so gaps mean something, or evenly,
 * so crowded decades stay legible; periods follow the same mapping either way.
 *
 * Cards never overlap. Horizontally they stack above the axis in levels, filled from the
 * right, which makes crowded runs climb to the left like a staircase: each card's leader
 * line then rises past the cards to its right instead of through them.
 */

import {
  CHART_PADDING,
  LINE_HEIGHT,
  layoutTitle,
  type Rect,
  type TitleBlock,
} from "@/lib/tool-kit/charts/layout";
import {
  maxTextWidth,
  type TextStyle,
  truncateText,
  wrapText,
} from "@/lib/tool-kit/charts/text";
import {
  type DateProblem,
  dateEnd,
  dateStart,
  type HistoricalDate,
  parseHistoricalDate,
  type TimeTick,
  timeTicks,
} from "@/lib/tool-kit/diagrams/dates";

/* ---------------------------------------------------------------- parsing */

export interface TimelineEvent {
  line: number;
  date: HistoricalDate;
  /** First day the date covers — where its dot goes. */
  day: number;
  label: string;
  description: string;
}

export interface TimelinePeriod {
  line: number;
  start: HistoricalDate;
  end: HistoricalDate;
  /** First day of the start date. */
  startDay: number;
  /** First day after the end date: `1914 – 1918` runs to the end of 1918. */
  endDay: number;
  label: string;
}

export type TimelineProblemKind = DateProblem | "endBeforeStart" | "noDate";

export interface TimelineProblem {
  line: number;
  text: string;
  kind: TimelineProblemKind;
}

export interface TimelineData {
  /** In date order; events on the same day keep the order they were typed in. */
  events: TimelineEvent[];
  periods: TimelinePeriod[];
  problems: TimelineProblem[];
}

const LINE_BREAK = /\r\n|\r|\n/;
const SPACED_RANGE = /\s+(?:–|—|-|to)\s+/;
const DASH_RANGE = /\s*[–—]\s*/;
const HYPHEN_RANGE = /^(.*\d.*?)\s*-\s*(\d{3,}.*)$/;

/** Date, label and description: split on tabs, else pipes, else the first two commas. */
export function splitTimelineLine(line: string): string[] {
  if (line.includes("\t")) {
    return line.split("\t").map((part) => part.trim());
  }
  if (line.includes("|")) {
    const [date = "", label = "", ...rest] = line.split("|");
    return [date.trim(), label.trim(), rest.join("|").trim()];
  }
  const first = line.indexOf(",");
  if (first === -1) {
    return [line.trim()];
  }
  const rest = line.slice(first + 1);
  const second = rest.indexOf(",");
  if (second === -1) {
    return [line.slice(0, first).trim(), rest.trim()];
  }
  return [
    line.slice(0, first).trim(),
    rest.slice(0, second).trim(),
    rest.slice(second + 1).trim(),
  ];
}

/** `1914 – 1918`, `1914 to 1918`, `1914–1918` or `1914-1918` → its two ends; else null. */
export function splitRange(text: string): [string, string] | null {
  for (const separator of [SPACED_RANGE, DASH_RANGE]) {
    const parts = text.split(separator);
    if (parts.length === 2 && parts[0].trim() && parts[1].trim()) {
      return [parts[0].trim(), parts[1].trim()];
    }
  }
  const match = HYPHEN_RANGE.exec(text.trim());
  return match ? [match[1].trim(), match[2].trim()] : null;
}

function readPeriod(
  range: [string, string],
  label: string,
  line: number,
  text: string
): TimelinePeriod | TimelineProblem {
  const start = parseHistoricalDate(range[0]);
  if (!start.ok) {
    return { line, text, kind: start.problem };
  }
  const end = parseHistoricalDate(range[1]);
  if (!end.ok) {
    return { line, text, kind: end.problem };
  }
  const startDay = dateStart(start.date);
  const endDay = dateEnd(end.date);
  if (endDay <= startDay) {
    return { line, text, kind: "endBeforeStart" };
  }
  return { line, start: start.date, end: end.date, startDay, endDay, label };
}

/** Read the timeline box: one event or period per line. */
export function parseTimeline(text: string): TimelineData {
  const events: TimelineEvent[] = [];
  const periods: TimelinePeriod[] = [];
  const problems: TimelineProblem[] = [];
  for (const [index, raw] of text.split(LINE_BREAK).entries()) {
    if (raw.trim() === "") {
      continue;
    }
    const line = index + 1;
    const [dateText = "", label = "", description = ""] =
      splitTimelineLine(raw);
    if (dateText === "") {
      problems.push({ line, text: raw.trim(), kind: "noDate" });
      continue;
    }
    const range = splitRange(dateText);
    if (range) {
      const period = readPeriod(range, label, line, raw.trim());
      if ("kind" in period) {
        problems.push(period);
      } else {
        periods.push(period);
      }
      continue;
    }
    const date = parseHistoricalDate(dateText);
    if (!date.ok) {
      problems.push({ line, text: raw.trim(), kind: date.problem });
      continue;
    }
    events.push({
      line,
      date: date.date,
      day: dateStart(date.date),
      label,
      description,
    });
  }
  events.sort((a, b) => a.day - b.day || a.line - b.line);
  periods.sort((a, b) => a.startDay - b.startDay || a.line - b.line);
  return { events, periods, problems };
}

/* ----------------------------------------------------------------- layout */

export type TimelineOrientation = "horizontal" | "vertical";
export type TimelineSpacing = "proportional" | "even";

export interface CardText {
  date: string;
  label: string;
  description: string;
}

export interface TimelineLayoutInput {
  /** In date order, as `parseTimeline` returns them. */
  events: readonly { day: number; text: CardText; circa: boolean }[];
  periods: readonly (LanePeriod & { label: string })[];
  orientation: TimelineOrientation;
  spacing: TimelineSpacing;
  /** Drawn inside the image; empty for none (an APA figure). */
  title: string;
  /** The words under a tick — the widget formats dates in the reader's language. */
  tickLabel: (tick: TimeTick, index: number) => string;
}

export interface CardLine {
  text: string;
  /** Baseline, relative to the card's top. */
  y: number;
  fontSize: number;
  bold: boolean;
  muted: boolean;
}

export interface CardLayout {
  index: number;
  rect: Rect;
  lines: CardLine[];
  /** The event's point on the axis. */
  dot: { x: number; y: number };
  /** From the dot to the card. */
  leader: [number, number][];
  circa: boolean;
}

export interface BandLayout {
  index: number;
  rect: Rect;
  /** The period's name, shortened to fit inside the band. */
  label: string;
  lane: number;
}

export interface TickLayout {
  x: number;
  y: number;
  label: string;
}

export interface TimelineLayout {
  width: number;
  height: number;
  orientation: TimelineOrientation;
  title: TitleBlock | null;
  axis: { x1: number; y1: number; x2: number; y2: number };
  cards: CardLayout[];
  bands: BandLayout[];
  ticks: TickLayout[];
}

export const TIMELINE_FONT = {
  date: 11,
  label: 12.5,
  description: 11,
  tick: 11,
  band: 11,
} as const;

/** Space inside a card around its text. */
export const CARD_PADDING = 7;
const LANE_HEIGHT = 20;
const LANE_GAP = 4;

/** A card's text, wrapped to `width`: date, label (up to three lines), description. */
function cardLines(text: CardText, width: number): CardLine[] {
  const inner = width - 2 * CARD_PADDING;
  const parts: {
    text: string;
    style: TextStyle;
    muted: boolean;
    max: number;
  }[] = [
    {
      text: text.date,
      style: { fontSize: TIMELINE_FONT.date, bold: true },
      muted: true,
      max: 1,
    },
    {
      text: text.label,
      style: { fontSize: TIMELINE_FONT.label, bold: true },
      muted: false,
      max: 3,
    },
    {
      text: text.description,
      style: { fontSize: TIMELINE_FONT.description },
      muted: true,
      max: 4,
    },
  ];
  const lines: CardLine[] = [];
  let y = CARD_PADDING;
  for (const part of parts) {
    if (part.text.trim() === "") {
      continue;
    }
    for (const line of wrapText(part.text, inner, part.style, part.max)) {
      y += part.style.fontSize * LINE_HEIGHT;
      lines.push({
        text: line,
        y: y - part.style.fontSize * 0.28,
        fontSize: part.style.fontSize,
        bold: part.style.bold ?? false,
        muted: part.muted,
      });
    }
  }
  return lines;
}

function cardHeight(lines: readonly CardLine[]): number {
  const last = lines.at(-1);
  return last ? last.y + last.fontSize * 0.28 + CARD_PADDING : 2 * CARD_PADDING;
}

/**
 * Maps a day to a position along the axis. Proportional: linear over the dates, with a
 * little room at each end. Even: event `i` sits at slot `i`, and any other day (a
 * period's ends) is interpolated between the events either side of it.
 */
export function axisMapping(
  eventDays: readonly number[],
  extraDays: readonly number[],
  spacing: TimelineSpacing,
  from: number,
  to: number
): (day: number) => number {
  const length = to - from;
  if (spacing === "even" && eventDays.length > 0) {
    const slot = length / eventDays.length;
    const knots = eventDays.map((day, index) => ({
      day,
      at: from + slot * (index + 0.5),
    }));
    return (day) => interpolate(knots, day, from, to);
  }
  const all = [...eventDays, ...extraDays];
  let min = Math.min(...all);
  let max = Math.max(...all);
  if (!(Number.isFinite(min) && Number.isFinite(max))) {
    return () => from + length / 2;
  }
  if (max - min < 2) {
    min -= 183;
    max += 183;
  }
  const pad = (max - min) * 0.04;
  const low = min - pad;
  const span = max + pad - low;
  return (day) => from + ((day - low) / span) * length;
}

function interpolate(
  knots: readonly { day: number; at: number }[],
  day: number,
  from: number,
  to: number
): number {
  const first = knots[0];
  const last = knots.at(-1) ?? first;
  if (day <= first.day || knots.length === 1) {
    return day < first.day ? from : first.at;
  }
  if (day >= last.day) {
    return day > last.day ? to : last.at;
  }
  for (let i = 1; i < knots.length; i++) {
    const right = knots[i];
    const left = knots[i - 1];
    if (day <= right.day) {
      const span = right.day - left.day;
      const share = span === 0 ? 1 : (day - left.day) / span;
      return left.at + share * (right.at - left.at);
    }
  }
  return last.at;
}

interface Level {
  spans: [number, number][];
  /** x positions of leaders passing through this level to higher cards. */
  leaders: number[];
}

const SPAN_GAP = 8;
const LEADER_CLEARANCE = 4;

function spanFree(level: Level | undefined, start: number, end: number) {
  return !(
    level?.spans.some(([a, b]) => start < b + SPAN_GAP && a < end + SPAN_GAP) ??
    false
  );
}

function leadersClear(level: Level | undefined, start: number, end: number) {
  return !(
    level?.leaders.some(
      (x) => x > start - LEADER_CLEARANCE && x < end + LEADER_CLEARANCE
    ) ?? false
  );
}

function pathClear(levels: readonly Level[], below: number, x: number) {
  return levels
    .slice(0, below)
    .every((level) =>
      level.spans.every(
        ([a, b]) => x <= a - LEADER_CLEARANCE || x >= b + LEADER_CLEARANCE
      )
    );
}

/** Clear leaders are sought on this many levels before a leader may pass behind a card. */
const CLEAR_LEVELS = 4;

/**
 * Levels for cards above a horizontal axis. Each card hangs to the right of its leader
 * like a flag, the leader running up its left edge.
 *
 * Cards are placed from the right, each on the lowest level where it overlaps no card
 * and its leader passes no card on the way up — so a run of close events climbs to the
 * left like a staircase. A dense run cannot climb forever with readable cards (each card
 * would have to clear every leader to its right), so after `CLEAR_LEVELS` levels a card
 * takes the lowest level with room and its leader passes behind the cards below it — at
 * a point inside them, never at their own left edge, which keeps the two apart.
 */
export function assignLevels(
  cards: readonly { x: number; width: number }[]
): number[] {
  const levels: Level[] = [];
  const result: number[] = new Array(cards.length);
  for (let i = cards.length - 1; i >= 0; i--) {
    const { x, width } = cards[i];
    const start = x - 1;
    const level =
      findLevel(levels, start, width, x, true) ??
      findLevel(levels, start, width, x, false) ??
      levels.length;
    while (levels.length <= level) {
      levels.push({ spans: [], leaders: [] });
    }
    levels[level].spans.push([start, start + width]);
    for (let below = 0; below < level; below++) {
      levels[below].leaders.push(x);
    }
    result[i] = level;
  }
  return result;
}

function findLevel(
  levels: readonly Level[],
  start: number,
  width: number,
  x: number,
  clearPath: boolean
): number | null {
  const highest = clearPath
    ? Math.min(levels.length, CLEAR_LEVELS - 1)
    : levels.length;
  const end = start + width;
  for (let level = 0; level <= highest; level++) {
    if (
      spanFree(levels[level], start, end) &&
      (!clearPath ||
        (leadersClear(levels[level], start, end) &&
          pathClear(levels, level, x)))
    ) {
      return level;
    }
  }
  return null;
}

export interface LanePeriod {
  start: number;
  end: number;
  /**
   * Length of the unit the end was written in — 366 days for a year. "509 BCE – 27 BCE"
   * and "27 BCE – 476" share the year 27 BCE; an overlap no longer than this is a shared
   * boundary, not two periods running side by side.
   */
  slack: number;
}

/**
 * Lanes for period bands, in start order: each band takes the first lane whose last band
 * has ended. Bands that only share a boundary unit keep one lane, and the earlier band
 * is cut back to meet the later one — so returned ends may be earlier than given.
 */
export function assignLanes(
  periods: readonly LanePeriod[]
): { lane: number; end: number }[] {
  const lanes: { end: number; slack: number; index: number }[] = [];
  const result = periods.map((period) => ({ lane: 0, end: period.end }));
  for (const [index, period] of periods.entries()) {
    let lane = lanes.findIndex((last) => last.end - last.slack <= period.start);
    if (lane === -1) {
      lane = lanes.length;
      lanes.push({ end: period.end, slack: period.slack, index });
    } else {
      const previous = lanes[lane];
      if (previous.end > period.start) {
        result[previous.index].end = period.start;
      }
      lanes[lane] = { end: period.end, slack: period.slack, index };
    }
    result[index].lane = lane;
  }
  return result;
}

function bandLabel(label: string, room: number): string {
  if (room < 18) {
    return "";
  }
  const text = truncateText(label, room, { fontSize: TIMELINE_FONT.band });
  return text === "…" ? "" : text;
}

/* ------------------------------------------------------- horizontal layout */

const HORIZONTAL_WIDTH = 760;
const HORIZONTAL_CARD = 138;
const LEVEL_GAP = 10;
const AXIS_GAP = 18;

function layoutHorizontal(input: TimelineLayoutInput): TimelineLayout {
  const width = HORIZONTAL_WIDTH;
  const title = layoutTitle(input.title, width);
  const left = CHART_PADDING + 6;
  const right = width - CHART_PADDING - 6;
  // Dates end a card's width short of the right edge, so the last card still hangs
  // to the right of its leader instead of being pushed back over its neighbours'.
  const map = axisMapping(
    input.events.map((event) => event.day),
    input.periods.flatMap((period) => [period.start, period.end]),
    input.spacing,
    left + 10,
    right - HORIZONTAL_CARD
  );
  const texts = input.events.map((event) =>
    cardLines(event.text, HORIZONTAL_CARD)
  );
  const xs = input.events.map((event) => map(event.day));
  const placement = assignLevels(
    xs.map((x) => ({ x, width: HORIZONTAL_CARD }))
  );
  const levelCount = Math.max(0, ...placement.map((level) => level + 1));
  const levelHeights = Array.from({ length: levelCount }, (_, level) =>
    Math.max(
      0,
      ...placement.map((placed, index) =>
        placed === level ? cardHeight(texts[index]) : 0
      )
    )
  );
  const top = CHART_PADDING + (title?.height ?? 0);
  const stack = levelHeights.reduce((sum, h) => sum + h + LEVEL_GAP, 0);
  const axisY = top + stack + AXIS_GAP;
  const levelBottom = (level: number) =>
    axisY -
    AXIS_GAP -
    levelHeights.slice(0, level).reduce((sum, h) => sum + h + LEVEL_GAP, 0);
  const cards: CardLayout[] = input.events.map((event, index) => {
    const height = cardHeight(texts[index]);
    const bottom = levelBottom(placement[index]);
    return {
      index,
      rect: {
        x: xs[index] - 1,
        y: bottom - height,
        width: HORIZONTAL_CARD,
        height,
      },
      lines: texts[index],
      dot: { x: xs[index], y: axisY },
      leader: [
        [xs[index], axisY],
        [xs[index], bottom],
      ],
      circa: event.circa,
    };
  });
  const ticks = horizontalTicks(input, map, left, right, axisY);
  const lanesTop = axisY + (ticks.length > 0 ? 30 : 12);
  const lanes = assignLanes(input.periods);
  const bands = input.periods.map((period, index) => {
    const x1 = Math.max(left, map(period.start));
    const x2 = Math.min(right, map(lanes[index].end));
    return {
      index,
      lane: lanes[index].lane,
      rect: {
        x: x1,
        y: lanesTop + lanes[index].lane * (LANE_HEIGHT + LANE_GAP),
        width: Math.max(2, x2 - x1),
        height: LANE_HEIGHT,
      },
      label: bandLabel(period.label, x2 - x1 - 8),
    };
  });
  const laneCount = Math.max(0, ...lanes.map((lane) => lane.lane + 1));
  const bottom =
    laneCount > 0
      ? lanesTop + laneCount * (LANE_HEIGHT + LANE_GAP)
      : axisY + (ticks.length > 0 ? 24 : 8);
  return {
    width,
    height: Math.ceil(bottom + CHART_PADDING),
    orientation: "horizontal",
    title,
    axis: { x1: left, y1: axisY, x2: right, y2: axisY },
    cards,
    bands,
    ticks,
  };
}

function horizontalTicks(
  input: TimelineLayoutInput,
  map: (day: number) => number,
  left: number,
  right: number,
  axisY: number
): TickLayout[] {
  if (input.spacing === "even") {
    return [];
  }
  const range = visibleRange(map, left, right);
  if (!range) {
    return [];
  }
  const ticks = timeTicks(
    range[0],
    range[1],
    Math.max(2, Math.floor((right - left) / 95))
  );
  return spreadLabels(
    ticks.map((tick, index) => ({
      x: map(tick.day),
      y: axisY,
      label: input.tickLabel(tick, index),
    })),
    "x"
  );
}

/** The days at the two ends of the axis, from the (linear) mapping. */
function visibleRange(
  map: (day: number) => number,
  from: number,
  to: number
): [number, number] | null {
  const unit = map(1) - map(0);
  if (!(unit > 0 && Number.isFinite(unit))) {
    return null;
  }
  const origin = map(0);
  return [(from - origin) / unit, (to - origin) / unit];
}

/** Drop every other tick label until neighbours no longer overlap. */
function spreadLabels(ticks: TickLayout[], along: "x" | "y"): TickLayout[] {
  const style = { fontSize: TIMELINE_FONT.tick };
  let kept = ticks;
  for (let pass = 0; pass < 6; pass++) {
    const crowded = kept.some((tick, index) => {
      const next = kept[index + 1];
      if (!next) {
        return false;
      }
      if (along === "y") {
        return next.y - tick.y < TIMELINE_FONT.tick * 1.6;
      }
      const room =
        (maxTextWidth([tick.label], style) +
          maxTextWidth([next.label], style)) /
          2 +
        10;
      return next.x - tick.x < room;
    });
    if (!crowded) {
      return kept;
    }
    kept = kept.filter((_, index) => index % 2 === 0);
  }
  return kept;
}

/* --------------------------------------------------------- vertical layout */

const VERTICAL_WIDTH = 640;
const MIN_AXIS = 360;

function layoutVertical(input: TimelineLayoutInput): TimelineLayout {
  const width = VERTICAL_WIDTH;
  const title = layoutTitle(input.title, width);
  const top = CHART_PADDING + (title?.height ?? 0) + 8;
  const lanes = assignLanes(input.periods);
  const laneCount = Math.max(0, ...lanes.map((lane) => lane.lane + 1));
  const laneWidth = laneCount * (LANE_HEIGHT + LANE_GAP);
  // Tick labels need the date range, which needs the axis length; a year label is at
  // most "10,000 BCE", so reserve room for that and measure nothing twice.
  const tickRoom = input.spacing === "even" ? 0 : 74;
  const axisX =
    CHART_PADDING + laneWidth + (laneCount > 0 ? 8 : 0) + tickRoom + 8;
  const cardX = axisX + 26;
  const cardWidth = width - CHART_PADDING - cardX;
  const texts = input.events.map((event) => cardLines(event.text, cardWidth));
  const heights = texts.map(cardHeight);
  const tallest = Math.max(0, ...heights);
  const stacked = heights.reduce((sum, h) => sum + h + 8, 0);
  const axisLength = Math.max(
    MIN_AXIS,
    input.spacing === "even" ? (tallest + 10) * input.events.length : stacked
  );
  const map = axisMapping(
    input.events.map((event) => event.day),
    input.periods.flatMap((period) => [period.start, period.end]),
    input.spacing,
    top + 6,
    top + axisLength - 6
  );
  let previousBottom = Number.NEGATIVE_INFINITY;
  const cards: CardLayout[] = input.events.map((event, index) => {
    const y = map(event.day);
    const cardTop = Math.max(y - 10, previousBottom + 8);
    previousBottom = cardTop + heights[index];
    const anchor = cardTop + Math.min(10, heights[index] / 2);
    return {
      index,
      rect: { x: cardX, y: cardTop, width: cardWidth, height: heights[index] },
      lines: texts[index],
      dot: { x: axisX, y },
      leader:
        Math.abs(anchor - y) < 0.5
          ? [
              [axisX, y],
              [cardX, y],
            ]
          : [
              [axisX, y],
              [axisX + 12, y],
              [cardX, anchor],
            ],
      circa: event.circa,
    };
  });
  const axisEnd = top + axisLength;
  const bands = input.periods.map((period, index) => {
    const y1 = Math.max(top, map(period.start));
    const y2 = Math.min(axisEnd, map(lanes[index].end));
    return {
      index,
      lane: lanes[index].lane,
      rect: {
        x: CHART_PADDING + lanes[index].lane * (LANE_HEIGHT + LANE_GAP),
        y: y1,
        width: LANE_HEIGHT,
        height: Math.max(2, y2 - y1),
      },
      label: bandLabel(period.label, y2 - y1 - 8),
    };
  });
  const range =
    input.spacing === "even" ? null : visibleRange(map, top, axisEnd);
  const ticks = range
    ? spreadLabels(
        timeTicks(
          range[0],
          range[1],
          Math.max(2, Math.floor(axisLength / 60))
        ).map((tick, index) => ({
          x: axisX,
          y: map(tick.day),
          label: truncateText(input.tickLabel(tick, index), tickRoom - 4, {
            fontSize: TIMELINE_FONT.tick,
          }),
        })),
        "y"
      )
    : [];
  return {
    width,
    height: Math.ceil(Math.max(axisEnd, previousBottom) + CHART_PADDING),
    orientation: "vertical",
    title,
    axis: { x1: axisX, y1: top, x2: axisX, y2: axisEnd },
    cards,
    bands,
    ticks,
  };
}

/** Place a timeline's cards, bands and ticks. */
export function layoutTimeline(input: TimelineLayoutInput): TimelineLayout {
  return input.orientation === "vertical"
    ? layoutVertical(input)
    : layoutHorizontal(input);
}
